import { useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import { soldOutOptionItemIds } from '../lib/availability'

type OptionGroupLike = { option_group?: { option_items?: { id: number; label?: string; sold_out?: boolean }[] | null } | null }

/**
 * The option-item twin of the wizards' meal-part rule (2026-10-27): a chosen option whose
 * `sold_out` is on — restored by the edit pre-fill, or flipped by the register's availability
 * poll while the wizard is open — cannot stay chosen (its button is disabled, so the cashier
 * could not change it) and would only fail at checkout. When one is found this calls `onDrop`
 * once with every sold-out id, after a toast naming what was dropped; the caller removes them
 * from its selection and returns to the customize step, so the step asks again — the cashier
 * may already be on a later step, and only the current step is validated before Add.
 */
export const useDropSoldOutOptions = (
  groups: OptionGroupLike[],
  selected: Record<number, number[]>,
  onDrop: (soldOut: Set<number>) => void,
) => {
  const onDropRef = useRef(onDrop)
  onDropRef.current = onDrop
  useEffect(() => {
    const soldOut = soldOutOptionItemIds(groups)
    if (soldOut.size === 0) return
    const chosen = Object.values(selected).flat().filter(id => soldOut.has(Number(id)))
    if (chosen.length === 0) return
    const labels = groups
      .flatMap(g => g.option_group?.option_items || [])
      .filter(it => chosen.includes(Number(it.id)))
      .map(it => String(it.label || ''))
    toast.error(`Sold out: ${labels.join(', ')} — choose again`)
    onDropRef.current(soldOut)
  }, [groups, selected])
}
